import { useEffect, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { checkDeploymentVersion, forceRefresh, verifyDeployment } from '@/utils/deploymentCheck';

interface VersionCheckerProps {
  checkInterval?: number; // in milliseconds, default 2 minutes (reduced frequency)
}

export const VersionChecker = ({ checkInterval = 2 * 60 * 1000 }: VersionCheckerProps) => {
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let retryCount = 0;
    const maxRetries = 3;

    const checkVersion = async () => {
      if (isChecking) return;
      
      setIsChecking(true);
      try {
        // Check for manual refresh trigger
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('force-refresh') === 'true') {
          console.log('🔄 Manual refresh triggered via URL parameter');
          forceRefresh();
          return;
        }

        const { hasUpdate, newVersion } = await checkDeploymentVersion();
        
        if (hasUpdate) {
          console.log('🚨 FORCING IMMEDIATE REFRESH - Version mismatch detected');
          
          // Verify deployment before refreshing
          const verification = await verifyDeployment();
          console.log('🔍 Deployment verification:', verification);
          
          // Show toast notification
          toast({
            title: "Update Available",
            description: `A new version (${newVersion}) is available. Refreshing automatically...`,
            action: (
              <Button 
                onClick={forceRefresh}
                size="sm"
                className="bg-primary hover:bg-primary/90"
              >
                Refresh Now
              </Button>
            ),
            duration: 0, // Don't auto-dismiss
          });
          
          // Force refresh after 2 seconds
          setTimeout(() => {
            console.log('🔄 Auto-refreshing browser...');
            forceRefresh();
          }, 2000);
          
          // Clear the interval once update is detected
          if (intervalId) {
            clearInterval(intervalId);
          }
        } else {
          // Reset retry count on successful check
          retryCount = 0;
          console.log('✅ Version check passed - no updates needed');
        }
      } catch (error) {
        console.error('❌ Version check failed:', error);
        retryCount++;
        
        if (retryCount >= maxRetries) {
          console.warn('⚠️ Version checking disabled after max retries');
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      } finally {
        setIsChecking(false);
      }
    };

    // Log version checker initialization
    console.log(`🔧 Version checker initialized - checking every ${checkInterval / 1000} seconds`);

    // Set up periodic checking only (no initial timeout to avoid loops)
    intervalId = setInterval(checkVersion, checkInterval);

    // Cleanup on unmount
    return () => {
      if (intervalId) {
        console.log('🛑 Version checker cleanup');
        clearInterval(intervalId);
      }
    };
  }, [checkInterval, isChecking]);

  return null; // This is a utility component that doesn't render anything
};